import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useJournal } from "./JournalContext";
import { todayParts } from "./dateUtils";

const DEFAULT_TIME = "21:00";
let lastNotifiedKey = "";

const storageKeyFor = (userId) => `journalReminder_${userId || "local"}`;

export function loadJournalReminder(userId) {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        enabled: !!parsed.enabled,
        time: typeof parsed.time === "string" && parsed.time.length >= 4 ? parsed.time.slice(0, 5) : DEFAULT_TIME,
      };
    }
  } catch (_) {}
  return { enabled: false, time: DEFAULT_TIME };
}

export function saveJournalReminder(userId, settings) {
  localStorage.setItem(storageKeyFor(userId), JSON.stringify(settings));
  window.dispatchEvent(new CustomEvent("journal-reminder-change"));
}

const nowHHMM = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

/**
 * Fires a "Did you write today?" notification at the chosen time when the tab is open.
 * Safe to mount once at the authenticated layout level.
 */
export function useJournalReminder() {
  const { user } = useAuth();
  const { entries, isLoading } = useJournal();
  const [settings, setSettings] = useState(() => loadJournalReminder(user?.id));
  const todayKey = todayParts().dateKey;
  const wroteToday = !!(entries?.[todayKey]?.entry?.trim() || entries?.[todayKey]?.summary?.trim());

  useEffect(() => {
    setSettings(loadJournalReminder(user?.id));
  }, [user?.id]);

  useEffect(() => {
    const onChange = () => setSettings(loadJournalReminder(user?.id));
    window.addEventListener("journal-reminder-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("journal-reminder-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [user?.id]);

  const update = useCallback(
    (patch) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        saveJournalReminder(user?.id, next);
        return next;
      });
    },
    [user?.id]
  );

  useEffect(() => {
    if (!settings.enabled || isLoading || typeof Notification === "undefined") return;

    const checkAndNotify = () => {
      const reminderTime = settings.time.length === 5 ? settings.time : settings.time.slice(0, 5);
      if (reminderTime !== nowHHMM()) return;

      const { dateKey } = todayParts();
      const notifyKey = `${dateKey}:${reminderTime}`;
      if (lastNotifiedKey === notifyKey) return;
      if (wroteToday) return;

      if (Notification.permission !== "granted") return;

      try {
        lastNotifiedKey = notifyKey;
        new Notification("ihkeely", {
          body: "Did you write today?",
          icon: "/favicon.ico",
          tag: "journal-reminder",
        });
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("Journal reminder failed:", e);
      }
    };

    const interval = setInterval(checkAndNotify, 30000);
    checkAndNotify();
    return () => clearInterval(interval);
  }, [settings.enabled, settings.time, wroteToday, isLoading]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    try {
      return await Notification.requestPermission();
    } catch (_) {
      return Notification.permission;
    }
  }, []);

  return {
    enabled: settings.enabled,
    time: settings.time,
    setEnabled: (enabled) => update({ enabled: !!enabled }),
    setTime: (time) => update({ time }),
    requestPermission,
    permission: typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  };
}
